import { useEffect } from 'react'
import { distinctUntilChanged, map, merge, mergeMap, scan } from 'rxjs'

import {
  AuthStateWithAccessToken,
  authSubject,
  latestAuthStateWithAccessToken$,
  remoteSignOut$,
} from '../../../auth/state'
import type { CartGetAction } from '../../reducers/cart-get-state'
import { actions, UseCartGetReducer } from '../useCartGetReducer'

export interface CartAuthKey {
  readonly isAuthenticated: boolean
  readonly subject: string | null
}

export const cartAuthKey = (authState: AuthStateWithAccessToken): CartAuthKey =>
  authState.isAuthenticated
    ? { isAuthenticated: true, subject: authSubject(authState.user) }
    : { isAuthenticated: false, subject: null }

export interface CartAuthHistory {
  readonly previous: CartAuthKey | null
  readonly wasAuthenticated: boolean
}

/**
 * Any sign-in after this tab has been signed in once is followed by `refetch`: the lazy customer
 * query still holds the previous session's cart and token, and `loggedIn` alone does not run it
 * again. A tab's first sign-in skips it, because the query has not been called yet.
 *
 * A sign-in that reaches a tab already showing a signed-out cart came from another tab (a local
 * login reloads the page), and that tab merges the guest cart, so this one must not.
 *
 * A sign-out after the first state is a local session expiry, which keeps the cart so checkout
 * can go on as a guest with it; a peer's sign-out resets the cart through `remoteSignOut$`.
 */
export const cartAuthActions = (
  { previous, wasAuthenticated }: CartAuthHistory,
  current: CartAuthKey,
): readonly CartGetAction[] =>
  !current.isAuthenticated
    ? previous === null
      ? [actions.loggedOut()]
      : []
    : [
        actions.loggedIn({ mergeGuestCart: previous === null || previous.isAuthenticated }),
        ...(wasAuthenticated ? [actions.refetch()] : []),
      ]

interface CartAuthStep extends CartAuthHistory {
  readonly actions: readonly CartGetAction[]
}

export const useAuthActions = ({ reducer }: { reducer: UseCartGetReducer }): void => {
  const [, dispatch] = reducer

  // Held for the life of the tab, so a sign-in, sign-out, or account switch made in another tab
  // reaches a cart that has already loaded.
  useEffect(() => {
    const authActions$ = latestAuthStateWithAccessToken$.pipe(
      map(cartAuthKey),
      distinctUntilChanged(
        (previous, current) =>
          previous.isAuthenticated === current.isAuthenticated &&
          previous.subject === current.subject,
      ),
      scan<CartAuthKey, CartAuthStep>(
        (step, key) => ({
          previous: key,
          wasAuthenticated: step.wasAuthenticated || key.isAuthenticated,
          actions: cartAuthActions(step, key),
        }),
        { previous: null, wasAuthenticated: false, actions: [] },
      ),
      mergeMap((step) => step.actions),
    )
    const subscription = merge(
      authActions$,
      remoteSignOut$.pipe(map(() => actions.loggedOut())),
    ).subscribe({
      next: dispatch,
      error: () => dispatch(actions.loggedOut()),
    })
    return () => subscription.unsubscribe()
  }, [dispatch])
}
